import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/site/Reveal";
import EventCard from "@/components/site/EventCard";
import PurchaseDialog from "@/components/site/PurchaseDialog";
import { Ticket, QrCode, ScanLine } from "lucide-react";

const STEPS = [
  { icon: Ticket, t: "Pick your night", d: "Choose an event and how many you're bringing." },
  { icon: QrCode, t: "Get your QR pass", d: "Instant ticket to your email and phone." },
  { icon: ScanLine, t: "Scan & enter", d: "We scan at the door. You walk straight in." },
];

export default function Tickets() {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = params.get("event");
    if (id && events.length) {
      const ev = events.find((e) => e.id === id);
      if (ev) setSelected(ev);
    }
  }, [params, events]);

  const close = () => {
    setSelected(null);
    params.delete("event");
    setParams(params, { replace: true });
  };

  return (
    <div className="pt-32 md:pt-44">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange mb-6">/ Buy Tickets</div>
        <h1 className="font-head font-extrabold uppercase leading-[0.82] tracking-tighter text-[16vw] md:text-[12vw]">
          <MaskedLines lines={["Secure", <>your <span className="text-brand-orange">spot</span></>]} />
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16 grid md:grid-cols-3 gap-6 md:gap-4">
        {STEPS.map((s, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="border border-brand-ink/10 p-7 h-full">
              <div className="w-12 h-12 bg-brand-orange text-white flex items-center justify-center">
                <s.icon size={22} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-ink/40 mt-6">0{i + 1}</div>
              <h3 className="font-head font-bold text-2xl mt-1 uppercase">{s.t}</h3>
              <p className="text-brand-ink/60 text-sm mt-2">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-24 pb-28 md:pb-40">
        <h2 className="font-head font-extrabold text-4xl md:text-6xl uppercase mb-10">Choose an event</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((e, i) => (
            <div key={e.id} onClick={() => setSelected(e)} className="cursor-pointer" data-testid={`ticket-select-${e.id}`}>
              <EventCard event={e} index={i} to="/tickets" />
            </div>
          ))}
        </div>
      </section>

      {selected && <PurchaseDialog event={selected} onClose={close} />}
    </div>
  );
}
